import { useTranslation } from "react-i18next";
import ArrowIcon from "../../icons/ArrowIcon";
import {
  ClearBtn,
  CloseButton,
  FilterBtn,
  InputWrapper,
  SearchHeader,
  SearchIconContainer,
  SearchInput,
  SearchWrapper,
} from "./styles";
import { useCallback, useEffect, useState, useMemo } from "react";
import useDebounce from "../../hooks/useDebounce";
import { useDispatch, useSelector } from "react-redux";
import SearchOutlineIcon from "../../icons/SearchOutlineIcon";
import FilterIcon from "../../icons/FilterIcon";
import SearchResult from "./SearchResult";
import SearchInitialState from "./SearchInitialState";
import { useHistory } from "react-router-dom";
import SearchFiltersPanel from "./SearchFiltersPanel";
import { setFilters } from "../../actions/filters";

const RECENT_KEY = "recent_search_terms";

const Search = ({ handleClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [recentSearches, setRecentSearches] = useState([]);
  // Debounce the raw input so we don't trigger searches on every keystroke
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { t } = useTranslation();
  const history = useHistory();
  const { local } = useSelector((state) => state.Local || {});
  const { filters } = useSelector((state) => state.Filters || { filters: {} });
  const dispatch = useDispatch();
  // Persisted (committed) filters from Redux
  const selectedCities = useMemo(
    () => filters?.cities || [],
    [filters?.cities]
  );
  const selectedTags = useMemo(() => filters?.tags || [], [filters?.tags]);
  const [showFilters, setShowFilters] = useState(false);
  // Local (uncommitted) working copies while the filters panel is open
  const [panelCities, setPanelCities] = useState(selectedCities);
  const [panelTags, setPanelTags] = useState(selectedTags);

  // Load recent searches
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setRecentSearches(JSON.parse(raw));
    } catch (e) {
      // ignore
    }
  }, []);

  const persistRecent = useCallback((term) => {
    if (!term || term.trim().length < 2) return;
    setRecentSearches((prev) => {
      const exists = prev.includes(term);
      const updated = exists ? prev : [term, ...prev].slice(0, 10);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  }, []);

  const handleSelectPlace = useCallback(
    (place) => {
      // navigate to POI page with locale prefix
      if (place?._id) {
        persistRecent(searchTerm);
        const path = local ? `/${local}/poi/${place._id}` : `/poi/${place._id}`;
        history.push(path);
        handleClose?.();
      }
    },
    [history, handleClose, searchTerm, persistRecent, local]
  );

  const applySearchChip = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleSearchChipClear = useCallback((term) => {
    setRecentSearches((prev) => {
      const updated = prev.filter((t) => t !== term);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  }, []);

  const clearSearch = () => setSearchTerm("");

  // When opening the panel, initialize working copies from persisted redux values
  useEffect(() => {
    if (showFilters) {
      setPanelCities(selectedCities);
      setPanelTags(selectedTags);
    }
    // We only want to re-init when panel is shown
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters]);

  // Commit filters to Redux when user applies
  const handleApplyFilters = useCallback(
    (cities, tags) => {
      dispatch(setFilters({ cities, tags }));
    },
    [dispatch]
  );

  // Cancel: discard all changes
  const handleCancelFilters = useCallback(() => {
    setPanelCities([]);
    setPanelTags([]);
    dispatch(setFilters({ cities: [], tags: [] }));
  }, [dispatch]);

  const activeFiltersCount = useMemo(
    () => selectedCities.length + selectedTags.length,
    [selectedCities.length, selectedTags.length]
  );

  return (
    <SearchWrapper>
      <SearchHeader>
        <CloseButton onClick={handleClose}>
          <ArrowIcon />
        </CloseButton>
        <InputWrapper>
          <SearchIconContainer>
            <SearchOutlineIcon width={27} height={27} />
          </SearchIconContainer>
          {searchTerm && <ClearBtn onClick={clearSearch}>X</ClearBtn>}
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            type="text"
            placeholder={t("searchPlaceholder")}
            onBlur={() => persistRecent(searchTerm)}
          />
        </InputWrapper>

        <FilterBtn
          type="button"
          data-filter-trigger
          onClick={() => setShowFilters((p) => !p)}
          active={showFilters}
          aria-expanded={showFilters}
          aria-controls="search-filters-panel"
        >
          <FilterIcon />

          <span className="filter-label">{t("filter")}</span>
          <span className="filter-count">({activeFiltersCount})</span>
          {activeFiltersCount > 0 && (
            <span className="filter-notification-dot" aria-hidden="true"></span>
          )}
        </FilterBtn>
      </SearchHeader>

      <SearchFiltersPanel
        open={showFilters}
        onClose={() => setShowFilters(false)}
        selectedCities={panelCities}
        selectedTags={panelTags}
        onChangeCities={setPanelCities}
        onChangeTags={setPanelTags}
        onApply={handleApplyFilters}
        onCancel={handleCancelFilters}
      />
      {searchTerm ? (
        <SearchResult
          searchTerm={debouncedSearchTerm}
          onSelect={handleSelectPlace}
          selectedCities={selectedCities}
          selectedTags={selectedTags}
        />
      ) : (
        <SearchInitialState
          recentSearches={recentSearches}
          onSearchChip={applySearchChip}
          onSearchChipClear={handleSearchChipClear}
          onSelectPlace={handleSelectPlace}
        />
      )}
    </SearchWrapper>
  );
};

export default Search;
